import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Eye,
  EyeOff,
  GripVertical,
  LayoutDashboard,
  RotateCcw,
  Save,
  Settings2,
} from 'lucide-react';
import {
  getAgentDashboardLayout,
  resetAgentDashboardLayout,
  updateAgentDashboardLayout,
} from '@/lib/api';

const tableNames = {
  referrals: 'Recent referrals',
  students: 'Linked students',
  commissions: 'Commission payments',
};

function moveItem(items, sourceId, targetId) {
  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  const targetIndex = items.findIndex((item) => item.id === targetId);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return items;
  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

function DraggableRow({ item, onDrop, onChange }) {
  return (
    <div
      draggable
      onDragStart={(event) => event.dataTransfer.setData('text/plain', item.id)}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(event.dataTransfer.getData('text/plain'), item.id);
      }}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-lg border border-[rgba(6,43,26,0.10)] bg-white p-2"
    >
      <GripVertical className="h-4 w-4 cursor-grab text-[#062B1A]/35" />
      <Input
        value={item.title ?? item.label}
        onChange={(event) => onChange({ ...(item.title !== undefined ? { title: event.target.value } : { label: event.target.value }) })}
        className="h-9 border-[rgba(6,43,26,0.12)]"
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={() => onChange({ visible: !item.visible })}
        className="h-9 w-9 text-[#062B1A]"
        aria-label={item.visible ? 'Hide item' : 'Show item'}
      >
        {item.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-[#062B1A]/35" />}
      </Button>
    </div>
  );
}

export default function AgencyDashboardEditor({ agentId }) {
  const [layout, setLayout] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!agentId) return undefined;
    let cancelled = false;
    getAgentDashboardLayout(agentId)
      .then(({ data }) => {
        if (!cancelled) setLayout(data);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) toast.error('Could not load the agency dashboard layout');
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const updateSection = (id, patch) => setLayout((current) => ({
    ...current,
    sections: current.sections.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));

  const updateColumn = (tableId, id, patch) => setLayout((current) => ({
    ...current,
    tables: {
      ...current.tables,
      [tableId]: current.tables[tableId].map((item) => (item.id === id ? { ...item, ...patch } : item)),
    },
  }));

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await updateAgentDashboardLayout(agentId, layout);
      setLayout(data);
      toast.success('Agency dashboard layout saved');
      setOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not save dashboard layout');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      setSaving(true);
      const { data } = await resetAgentDashboardLayout(agentId);
      setLayout(data);
      toast.success('Agency dashboard restored to its default layout');
    } catch (error) {
      console.error(error);
      toast.error('Could not reset dashboard layout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            <LayoutDashboard className="h-4 w-4 text-[#D4AF37]" />
            Agency Dashboard Layout
          </CardTitle>
          <p className="mt-1 text-sm text-[#062B1A]/55">
            Reorder dashboard sections and choose how agency tables are displayed.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setOpen((value) => !value)} className="border-[#D4AF37]/40 text-[#8A6508]">
          <Settings2 className="mr-2 h-4 w-4" />
          {open ? 'Close editor' : 'Edit dashboard'}
        </Button>
      </CardHeader>

      {open && layout && (
        <CardContent className="space-y-6 border-t border-[rgba(6,43,26,0.08)] pt-5">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <h4 className="text-sm font-semibold text-[#062B1A]">Dashboard sections</h4>
              <p className="mb-3 mt-1 text-xs text-[#062B1A]/50">Drag rows to reorder them. Rename or hide any section.</p>
              <div className="space-y-2">
                {layout.sections.map((section) => (
                  <DraggableRow
                    key={section.id}
                    item={section}
                    onDrop={(sourceId, targetId) => setLayout((current) => ({
                      ...current,
                      sections: moveItem(current.sections, sourceId, targetId),
                    }))}
                    onChange={(patch) => updateSection(section.id, patch)}
                  />
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-4">
              <h4 className="text-sm font-semibold text-[#062B1A]">Layout preview</h4>
              <div className="mt-3 space-y-2">
                {layout.sections.filter((section) => section.visible).map((section, index) => (
                  <div key={section.id} className="rounded-lg border border-[#D4AF37]/25 bg-white px-3 py-2 text-sm text-[#062B1A]">
                    <span className="mr-2 text-xs font-semibold text-[#D4AF37]">{index + 1}</span>
                    {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-[#062B1A]">Edit tables</h4>
            <p className="mb-3 mt-1 text-xs text-[#062B1A]/50">Drag columns to reorder them, rename headings, or hide columns.</p>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {Object.entries(layout.tables).map(([tableId, columns]) => (
                <div key={tableId} className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
                  <p className="mb-2 text-sm font-semibold text-[#062B1A]">{tableNames[tableId] || tableId}</p>
                  <div className="space-y-2">
                    {columns.map((column) => (
                      <DraggableRow
                        key={column.id}
                        item={column}
                        onDrop={(sourceId, targetId) => setLayout((current) => ({
                          ...current,
                          tables: {
                            ...current.tables,
                            [tableId]: moveItem(current.tables[tableId], sourceId, targetId),
                          },
                        }))}
                        onChange={(patch) => updateColumn(tableId, column.id, patch)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={reset} disabled={saving}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset default
            </Button>
            <Button type="button" onClick={save} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save dashboard'}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
